import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import Helper from "../../helper";
import Appconfig from "../../config/config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AccountStatement.css";

const REPORT_TYPES = [
  { value: "", label: "Select Report Type" },
  { value: "deposit-withdraw", label: "Deposit/Withdraw Reports" },
  { value: "sport", label: "Sport Report" },
  { value: "casino", label: "Casino Reports" },
  { value: "third-party-casino", label: "Third-Party Casino Reports" },
];

const AccountDateInput = React.forwardRef(
  (
    {
      value,
      onClick,
      placeholder,
      ariaLabel,
    },
    ref
  ) => {
    return (
      <button
        type="button"
        ref={ref}
        className="account-custom-date-input"
        onClick={onClick}
        aria-label={ariaLabel}
      >
        <span>
          {value || placeholder}
        </span>

        <i
          className="far fa-calendar-alt"
          aria-hidden="true"
        />
      </button>
    );
  }
);

AccountDateInput.displayName =
  "AccountDateInput";

const getDefaultFilters = (userId) => ({
  user_id: userId,
  report_type: "deposit-withdraw",
  type: "0",
  from_date: moment().subtract(7, "days").format("YYYY-MM-DD"),
  to_date: moment().format("YYYY-MM-DD"),
  game_type: "FREE_CHIP",
});

/*
 * Existing API type/game_type values are retained for deposit/withdraw.
 * If the backend uses different values for the other report categories,
 * change only this map—not the form or table layout.
 */
const REPORT_PAYLOAD = {
  "deposit-withdraw": {
    type: "0",
    game_type: "FREE_CHIP",
  },

  sport: {
    type: "1",
    game_type: "SPORTS",
  },

  casino: {
    type: "1",
    game_type: "CASINO",
  },

  "third-party-casino": {
    type: "1",
    game_type: "WCO_CASINO",
  },
};

const AccountStatement = () => {
  const userInfo = Helper();
  const [formData, setFormData] = useState(() =>
    getDefaultFilters(userInfo?._id)
  );
  const [data, setData] = useState([]);
  const [entries, setEntries] = useState(10);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBetRecord, setSelectedBetRecord] = useState(null);
  const [isBetDetailsModalOpen, setIsBetDetailsModalOpen] =
    useState(false);
  const [betDetails, setBetDetails] = useState([]);
  const [isBetDetailsLoading, setIsBetDetailsLoading] =
    useState(false);
  const [betDetailsError, setBetDetailsError] = useState("");
  const [betDetailsFilter, setBetDetailsFilter] =
    useState("all");

  const getAccountStatement = async (filters = formData) => {
    try {
      setIsLoading(true);

      const selectedReportPayload =
        REPORT_PAYLOAD[filters.report_type] ||
        REPORT_PAYLOAD["deposit-withdraw"];

      const requestPayload = {
        ...filters,
        user_id: userInfo?._id,
        ...selectedReportPayload,
      };

      console.log(
        "Account statement request:",
        requestPayload
      );

      const response = await axios({
        method: "post",
        url: `${Appconfig.apiUrl}reports/aurafilteraccountStatement`,
        headers: { "Content-Type": "application/json" },
        data: requestPayload,
      });

      setData(
        Array.isArray(response?.data?.resultData)
          ? response.data.resultData
          : []
      );
    } catch (error) {
      console.error("Unable to load account statement:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialFilters = getDefaultFilters(userInfo?._id);
    setFormData(initialFilters);
    getAccountStatement(initialFilters);
    // Initial statement should load once for the logged-in user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?._id]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    if (name === "report_type") {
      const payload =
        REPORT_PAYLOAD[value] ||
        REPORT_PAYLOAD["deposit-withdraw"];

      setFormData((current) => ({
        ...current,
        report_type: value,
        ...payload,
      }));
      return;
    }

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleFromDateChange = (selectedDate) => {
    if (!selectedDate) {
      return;
    }

    const formattedDate = moment(
      selectedDate
    ).format("YYYY-MM-DD");

    setFormData((current) => {
      /*
       * Agar newly selected from-date existing
       * to-date ke baad hai, toh to-date ko bhi
       * same date par set kar dega.
       */
      const currentToDate = moment(
        current.to_date,
        "YYYY-MM-DD",
        true
      );

      const nextToDate =
        currentToDate.isValid() &&
          moment(selectedDate).isAfter(
            currentToDate,
            "day"
          )
          ? formattedDate
          : current.to_date;

      return {
        ...current,
        from_date: formattedDate,
        to_date: nextToDate,
      };
    });
  };

  const handleToDateChange = (selectedDate) => {
    if (!selectedDate) {
      return;
    }

    setFormData((current) => ({
      ...current,
      to_date: moment(selectedDate).format(
        "YYYY-MM-DD"
      ),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    getAccountStatement();
  };

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return data;

    return data.filter((record) =>
      [
        record?.createdAt,
        record?.remarks,
        record?.remark,
        record?.transaction_type,
        record?.amount,
        record?.credit,
        record?.debit,
        record?.balance,
        record?.pts,
        record?.points,
        record?.type,
        record?.event_type,
        record?.game_name,
        record?.provider_name,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query)
      )
    );
  }, [data, search]);

  const visibleData = filteredData.slice(0, entries);

  const getStatementRowClass = (record) => {
    const bankingType = String(
      record?.banking_type || ""
    ).toLowerCase();

    const transactionType = String(
      record?.transaction_type || ""
    ).toLowerCase();

    const remarks = String(
      record?.remarks || record?.remark || ""
    ).toLowerCase();

    // Sabse pehle explicit banking_type check hoga
    if (bankingType === "deposit") {
      return "statement-row-deposit";
    }

    if (bankingType === "withdraw") {
      return "statement-row-withdraw";
    }

    // Purane records ke remarks se type identify karega
    if (remarks.includes("deposit")) {
      return "statement-row-deposit";
    }

    if (
      remarks.includes("withdraw") ||
      remarks.includes("withdrawl")
    ) {
      return "statement-row-withdraw";
    }

    // Final fallback
    if (transactionType === "credit") {
      return "statement-row-deposit";
    }

    if (transactionType === "debit") {
      return "statement-row-withdraw";
    }

    return "";
  };

  const formatStatementNumber = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return "-";
    }

    return Math.round(numberValue).toLocaleString("en-IN");
  };

  const getSportName = (eventType) => {
    const sportNames = {
      1: "Soccer",
      2: "Tennis",
      4: "Cricket",
      7: "Horse Racing",
      4339: "Greyhound Racing",
    };

    return sportNames[eventType] || "Sports";
  };

  const getWinnerName = (bets) => {
    const winningBet = bets.find(
      (bet) => bet?.result_name
    );

    if (winningBet?.result_name) {
      return winningBet.result_name;
    }

    const resultId = bets.find(
      (bet) => bet?.result_id
    )?.result_id;

    const runners = bets.find(
      (bet) => Array.isArray(bet?.runners)
    )?.runners;

    const winner = runners?.find(
      (runner) =>
        String(runner?.selection_id) === String(resultId)
    );

    return winner?.runner_name || "-";
  };

  const formatIpAddress = (ipAddress) => {
    return String(ipAddress || "-").replace("::ffff:", "");
  };

  const filteredBetDetails = useMemo(() => {
    if (betDetailsFilter === "all") {
      return betDetails;
    }

    if (betDetailsFilter === "back") {
      return betDetails.filter(
        (bet) =>
          Number(bet?.is_back) === 1 ||
          bet?.is_back === true
      );
    }

    if (betDetailsFilter === "lay") {
      return betDetails.filter(
        (bet) =>
          Number(bet?.is_back) === 0 ||
          bet?.is_back === false
      );
    }

    if (betDetailsFilter === "deleted") {
      return betDetails.filter((bet) => {
        const status = String(bet?.status || "").toLowerCase();
        const isDeleted = String(
          bet?.is_delete || ""
        ).toLowerCase();

        return (
          status === "deleted" ||
          status === "void" ||
          isDeleted === "yes"
        );
      });
    }

    return betDetails;
  }, [betDetails, betDetailsFilter]);

  const canOpenBetDetails = (record) => {
    const recordType = String(record?.type || "")
      .trim()
      .toLowerCase();

    const remark = String(
      record?.remarks ||
      record?.remark ||
      record?.game_name ||
      ""
    ).toLowerCase();

    return (
      recordType === "betting" ||
      Boolean(record?.match_id) ||
      Boolean(record?.market_id) ||
      remark.includes("cricket") ||
      remark.includes("tennis") ||
      remark.includes("soccer") ||
      remark.includes("football") ||
      remark.includes("horse racing")
    );
  };

  const loadBetDetails = async (record) => {
    try {
      setIsBetDetailsLoading(true);
      setBetDetailsError("");
      setBetDetails([]);

      const response = await axios.post(
        `${Appconfig.apiUrl}reports/auraProfitLossBetHistory`,
        {
          user_id: userInfo?._id,
          match_id: record?.match_id,
          market_id: record?.market_id,
          market_name: record?.market_name || "",
          event_type: record?.event_type,
          from_date: formData.from_date,
          to_date: formData.to_date,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.result === 1) {

        console.log("Bet Details API response:", response?.data?.resultData);

        setBetDetails(response?.data?.resultData || []);
        return;
      }

      setBetDetailsError(
        response?.data?.resultMessage ||
        "Bet details could not be loaded."
      );
    } catch (error) {
      console.error("Unable to load bet details:", error);

      setBetDetailsError(
        error?.response?.data?.resultMessage ||
        "Bet details could not be loaded."
      );
    } finally {
      setIsBetDetailsLoading(false);
    }
  };

  const handleBetRemarkClick = (record) => {
    if (!canOpenBetDetails(record)) {
      return;
    }

    setSelectedBetRecord(record);
    setIsBetDetailsModalOpen(true);
    loadBetDetails(record);
  };

  const closeBetDetailsModal = () => {
    setSelectedBetRecord(null);
    setIsBetDetailsModalOpen(false);
    setBetDetails([]);
    setBetDetailsError("");
    setBetDetailsFilter("all");
  };

  const getStatementRemark = (record) => {
    return (
      record?.remarks ||
      record?.remark ||
      record?.game_name ||
      (record?.type === "WCO_CASINO"
        ? "Casino Profit/Loss"
        : "-")
    );
  };

  return (
    <main className="account-statement-page">
      <section className="account-statement-panel">
        <h1 className="account-statement-heading">
          Account Statement
        </h1>

        <form
          className="account-statement-filters"
          onSubmit={handleSubmit}
        >
          <div className="account-date-field">
            <DatePicker
              selected={
                formData.from_date
                  ? moment(
                    formData.from_date,
                    "YYYY-MM-DD",
                    true
                  ).toDate()
                  : null
              }
              onChange={handleFromDateChange}
              dateFormat="dd/MM/yyyy"
              maxDate={new Date()}
              placeholderText="dd/mm/yyyy"
              name="from_date"
              autoComplete="off"
              showPopperArrow
              popperPlacement="bottom-start"
              wrapperClassName="account-date-wrapper"
              calendarClassName="account-reference-calendar"
              customInput={
                <AccountDateInput
                  ariaLabel="From date"
                />
              }
            />
          </div>

          <div className="account-date-field">
            <DatePicker
              selected={
                formData.to_date
                  ? moment(
                    formData.to_date,
                    "YYYY-MM-DD",
                    true
                  ).toDate()
                  : null
              }
              onChange={handleToDateChange}
              dateFormat="dd/MM/yyyy"
              minDate={
                formData.from_date
                  ? moment(
                    formData.from_date,
                    "YYYY-MM-DD",
                    true
                  ).toDate()
                  : undefined
              }
              maxDate={new Date()}
              placeholderText="dd/mm/yyyy"
              name="to_date"
              autoComplete="off"
              showPopperArrow
              popperPlacement="bottom-start"
              wrapperClassName="account-date-wrapper"
              calendarClassName="account-reference-calendar"
              customInput={
                <AccountDateInput
                  ariaLabel="To date"
                />
              }
            />
          </div>

          <select
            name="report_type"
            value={formData.report_type}
            onChange={handleFieldChange}
            aria-label="Report type"
          >
            {REPORT_TYPES.map((option) => (
              <option key={option.value || "empty"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button type="submit" disabled={isLoading}>
            {isLoading ? "Loading..." : "Submit"}
          </button>
        </form>

        <div className="account-statement-toolbar">
          <label className="account-statement-entries">
            <span>Show</span>
            <select
              value={entries}
              onChange={(event) =>
                setEntries(Number(event.target.value))
              }
            >
              {[10, 25, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <span>Entries</span>
          </label>

          <label className="account-statement-search">
            <span>Search:</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`${filteredData.length} records...`}
            />
          </label>
        </div>

        <div className="account-statement-table-wrap">
          <table className="account-statement-table">
            <thead>
              <tr>
                <th className="statement-col-date">Date</th>
                <th className="statement-col-serial">Sr no</th>
                <th className="statement-col-credit">Credit</th>
                <th className="statement-col-debit">Debit</th>
                <th className="statement-col-points">Pts</th>
                <th className="statement-col-remark">Remark</th>
              </tr>
            </thead>

            <tbody>
              {visibleData.length > 0 ? (
                visibleData.map((record, index) => (
                  <tr
                    key={record?._id || `${record?.createdAt}-${index}`}
                    className={getStatementRowClass(record)}
                  >
                    <td className="statement-col-date">
                      {record?.createdAt
                        ? moment(record.createdAt).format(
                          "DD/MM/YYYY HH:mm:ss"
                        )
                        : "-"}
                    </td>

                    <td className="statement-col-serial">
                      {index + 1}
                    </td>

                    <td className="statement-col-credit statement-credit">
                      {record?.credit !== undefined &&
                        record?.credit !== null
                        ? formatStatementNumber(Math.abs(Number(record.credit)))
                        : String(record?.transaction_type || "").toLowerCase() ===
                          "credit"
                          ? formatStatementNumber(Math.abs(Number(record?.amount || 0)))
                          : "0"}
                    </td>

                    <td className="statement-col-debit statement-debit">
                      {record?.debit !== undefined &&
                        record?.debit !== null
                        ? Number(record.debit) === 0
                          ? "0"
                          : `-${formatStatementNumber(Math.abs(Number(record.debit)))}`
                        : String(record?.transaction_type || "").toLowerCase() === "debit"
                          ? Number(record?.amount || 0) === 0
                            ? "0"
                            : `-${formatStatementNumber(
                              Math.abs(Number(record?.amount || 0))
                            )}`
                          : "0"}
                    </td>

                    <td className="statement-col-points">
                      {formatStatementNumber(
                        record?.pts ??
                        record?.points ??
                        record?.balance ??
                        record?.available_balance ??
                        record?.closing_balance
                      )}
                    </td>

                    <td className="statement-col-remark">
                      {canOpenBetDetails(record) ? (
                        <button
                          type="button"
                          className="statement-remark-button"
                          onClick={() => handleBetRemarkClick(record)}
                          title="View bet details"
                        >
                          {getStatementRemark(record)}
                        </button>
                      ) : (
                        <span className="statement-remark-text">
                          {getStatementRemark(record)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="statement-empty">
                    {isLoading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isBetDetailsModalOpen && selectedBetRecord && (
          <div
            className="bet-details-modal-overlay"
            onClick={closeBetDetailsModal}
          >
            <section
              className="bet-details-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bet-details-modal-title"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="bet-details-modal-header">
                <h2 id="bet-details-modal-title">Result</h2>

                <button
                  type="button"
                  className="bet-details-modal-close"
                  onClick={closeBetDetailsModal}
                  aria-label="Close bet details"
                >
                  ×
                </button>
              </header>

              <div className="bet-details-modal-body">
                <div className="bet-details-event-info">
                  <div>
                    <div className="bet-details-event-name">
                      {getSportName(
                        betDetails[0]?.event_type ||
                        selectedBetRecord?.event_type
                      )}{" "}
                      -&gt;{" "}
                      {betDetails[0]?.event_name ||
                        selectedBetRecord?.event_name ||
                        "-"}{" "}
                      -&gt;{" "}
                      {betDetails[0]?.market_name ||
                        selectedBetRecord?.market_name ||
                        "-"}
                    </div>

                    <div className="bet-details-winner">
                      Winner: {getWinnerName(betDetails)}
                    </div>
                  </div>

                  <div className="bet-details-game-time">
                    Game Time:{" "}
                    {betDetails[0]?.game_time
                      ? moment(
                        betDetails[0].game_time,
                        "YYYY/MM/DD HH:mm:ss"
                      ).format("DD/MM/YYYY HH:mm:ss")
                      : "-"}
                  </div>
                </div>

                <div className="bet-details-controls">
                  <div
                    className="bet-details-filter-group"
                    role="radiogroup"
                    aria-label="Bet type filter"
                  >
                    <label>
                      <input
                        type="radio"
                        name="bet-details-filter"
                        checked={betDetailsFilter === "all"}
                        onChange={() => setBetDetailsFilter("all")}
                      />
                      <span>All</span>
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="bet-details-filter"
                        checked={betDetailsFilter === "back"}
                        onChange={() => setBetDetailsFilter("back")}
                      />
                      <span>Back</span>
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="bet-details-filter"
                        checked={betDetailsFilter === "lay"}
                        onChange={() => setBetDetailsFilter("lay")}
                      />
                      <span>Lay</span>
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="bet-details-filter"
                        checked={betDetailsFilter === "deleted"}
                        onChange={() => setBetDetailsFilter("deleted")}
                      />
                      <span>Deleted</span>
                    </label>
                  </div>

                  <div className="bet-details-summary">
                    <span>
                      Total Bets: <b>{filteredBetDetails.length}</b>
                    </span>

                    <span>
                      Total Amount:{" "}
                      <b>
                        {formatStatementNumber(
                          filteredBetDetails.reduce(
                            (total, bet) =>
                              total + Number(bet?.totalPL || 0),
                            0
                          )
                        )}
                      </b>
                    </span>
                  </div>
                </div>

                <div className="bet-details-table-wrap">
                  <table className="bet-details-table">
                    <thead>
                      <tr>
                        <th>Nation</th>
                        <th>Rate</th>
                        <th>Bhav</th>
                        <th>Amount</th>
                        <th>Win</th>
                        <th>Date</th>
                        <th>IP Address</th>
                        <th>Browser Details</th>
                        <th className="bet-details-select-column">
                          <input
                            type="checkbox"
                            disabled
                            aria-label="Select all bets"
                          />
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {isBetDetailsLoading ? (
                        <tr>
                          <td colSpan="9" className="bet-details-message">
                            Loading bet details...
                          </td>
                        </tr>
                      ) : betDetailsError ? (
                        <tr>
                          <td colSpan="9" className="bet-details-message is-error">
                            {betDetailsError}
                          </td>
                        </tr>
                      ) : filteredBetDetails.length > 0 ? (
                        filteredBetDetails.map((bet, index) => (
                          <tr
                            key={bet?._id || `${bet?.createdAt}-${index}`}
                            className={
                              Number(bet?.is_back) === 1 || bet?.is_back === true
                                ? "bet-details-row-back"
                                : "bet-details-row-lay"
                            }
                          >
                            <td>
                              {bet?.runner_name ||
                                bet?.place_name ||
                                bet?.selection_name ||
                                "-"}
                            </td>

                            <td>{bet?.price_val ?? "-"}</td>

                            <td>
                              {bet?.fancy_size ?? bet?.bhav ?? "-"}
                            </td>

                            <td>
                              {formatStatementNumber(
                                bet?.stake ?? bet?.amount ?? 0
                              )}
                            </td>

                            <td>
                              {formatStatementNumber(bet?.totalPL ?? 0)}
                            </td>

                            <td>
                              {bet?.createdAt
                                ? moment(bet.createdAt).format(
                                  "DD/MM/YYYY HH:mm:ss"
                                )
                                : "-"}
                            </td>

                            <td>{formatIpAddress(bet?.ip_address)}</td>

                            <td>
                              {bet?.browser_details ||
                                bet?.user_agent ||
                                "Not available"}
                            </td>

                            <td className="bet-details-select-column">
                              <input
                                type="checkbox"
                                disabled
                                aria-label={`Select ${bet?.runner_name || "bet"
                                  }`}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="bet-details-message">
                            No bet details found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>
        )}

      </section>
    </main>
    // <Footer />
  );
};

export default AccountStatement;
